import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { devLog } from '@/utils/logger';

/**
 * Quote structure from CSV
 * Format: Author,Quote,Category
 */
export interface Quote {
  author: string;
  text: string;
  category: string;
}

/**
 * Supported languages for quotes
 */
const SUPPORTED_LANGUAGES = ['en', 'de', 'es', 'fr', 'it', 'nl', 'sv', 'tr', 'hr'];

/**
 * Cache to prevent duplicate fetches (StrictMode double-mount)
 */
const quotesCache: Map<string, Promise<Quote[]>> = new Map();

/**
 * Parse CSV line handling quoted values
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * useQuotes Hook
 *
 * Loads inspirational quotes from CSV files based on user's language.
 * Falls back to English if language not available.
 *
 * @returns {Quote | null} Random quote or null while loading
 */
export function useQuotes(): Quote | null {
  const { i18n } = useTranslation();
  const [quote, setQuote] = useState<Quote | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadQuotes = async () => {
      // Get user's language, fallback to English
      const userLang = i18n.language?.split('-')[0] || 'en';
      const lang = SUPPORTED_LANGUAGES.includes(userLang) ? userLang : 'en';

      try {
        // Check cache first (prevents duplicate fetches in StrictMode)
        let quotesPromise = quotesCache.get(lang);

        if (!quotesPromise) {
          // Create and cache the fetch promise
          quotesPromise = (async () => {
            const response = await fetch(`/i18n/inspo-${lang}.csv`);
            if (!response.ok) {
              throw new Error(`Failed to load quotes for ${lang}`);
            }

            const text = await response.text();
            const lines = text.split('\n').filter((line) => line.trim());

            if (lines.length === 0) {
              throw new Error('No quotes found');
            }

            // Parse all quotes
            return lines.map((line) => {
              const [author, text, category] = parseCSVLine(line);
              return {
                author: author || 'Unknown',
                text: text || '',
                category: category || 'Quote',
              };
            }).filter((q) => q.text); // Filter out empty quotes
          })();

          quotesCache.set(lang, quotesPromise);
        }

        const quotes = await quotesPromise;

        // Select random quote
        if (isMounted) {
          const randomIndex = Math.floor(Math.random() * quotes.length);
          setQuote(quotes[randomIndex]);
        }
      } catch (error) {
        devLog.error('[useQuotes] Failed to load quotes:', error);
        // Fallback quote
        if (isMounted) {
          setQuote({
            author: 'Seth Godin',
            text: 'Marketing is no longer about the stuff you make, but about the stories you tell.',
            category: 'Quote',
          });
        }
      }
    };

    loadQuotes();

    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  return quote;
}
